import { z } from "zod/v4";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import type { Unit } from "@corpuscomplete/core/common/unit";
import { typedEntries } from "@corpuscomplete/core/utils";

export namespace MacroDistribution {
  const gPerKgSchema = z.object({
    type: z.literal("gPerKg"),
    gPerKg: z.number(),
  });

  const minimumSchema = z.union([
    gPerKgSchema,
    z.object({
      type: z.literal("absolute"),
      value: z.number(),
    }),
  ]);
  type Minimum = z.infer<typeof minimumSchema>;

  export const suggestedMinimums: Record<Nutrient.MacroKey, () => Minimum | undefined> = {
    // Existence of keto diet makes this not relevant? Or at least unclear on how to use?
    carbohydrate: () => undefined,
    protein: () => ({ type: "gPerKg", gPerKg: 1.2 }),
    // Should this be 0.5?
    fat: () => ({ type: "gPerKg", gPerKg: 0.4 }),
  };

  export const schema = z
    .record(
      Nutrient.macroKeySchema,
      z.union([
        z.object({
          type: z.literal("leftoverPercentage"),
          leftoverPercentage: z.number().positive(),
          minimum: minimumSchema.optional(),
        }),
        gPerKgSchema,
      ]),
    )
    .check((ctx) => {
      let gPerKgCount = 0;
      let leftoverPercentageTotal = 0;
      for (const value of Object.values(ctx.value)) {
        if (value.type === "gPerKg") {
          gPerKgCount++;
        } else {
          leftoverPercentageTotal += value.leftoverPercentage;
        }
      }
      if (gPerKgCount === 3) {
        ctx.issues.push({
          code: "custom",
          message: "Must have at most one gPerKg value",
          input: ctx.value,
        });
      }
      if (leftoverPercentageTotal !== 100) {
        ctx.issues.push({
          code: "custom",
          message: "Must have a total of 100% leftoverPercentage values",
          input: ctx.value,
        });
      }
    });
  export type Info = z.infer<typeof schema>;

  export const calculateNutrientTargets = (
    macroDistribution: Info,
    totalCalories: number,
    weight: Unit.Quantity<"mass", "kg">,
  ): Nutrient.Targets | Error => {
    const result = {
      energy: totalCalories,
      carbohydrate: 0,
      protein: 0,
      fat: 0,
    };

    let gPerKgCalories = 0;
    for (const [key, distribution] of typedEntries(macroDistribution)) {
      if (distribution.type === "gPerKg") {
        const macroGrams = distribution.gPerKg * weight.quantity;
        result[key] = macroGrams;
        gPerKgCalories += macroGrams * Nutrient.macroKcalPerGram[key];
      }
    }

    const leftoverCalories = totalCalories - gPerKgCalories;
    if (leftoverCalories < 0) {
      return new Error("gPerKg distributions exceed total calories");
    }

    let leftoverCaloriesAfterMinimums = leftoverCalories;
    let leftoverPercentageAfterMinimums = 100;
    const nonMinimumLeftoverKeys: Nutrient.MacroKey[] = [];
    for (const [key, distribution] of typedEntries(macroDistribution)) {
      if (distribution.type === "leftoverPercentage") {
        const calories = (leftoverCalories * distribution.leftoverPercentage) / 100;
        const macroGrams = calories / Nutrient.macroKcalPerGram[key];

        let minimumMacroGrams = 0;
        if (distribution.minimum) {
          minimumMacroGrams =
            distribution.minimum.type === "gPerKg"
              ? distribution.minimum.gPerKg * weight.quantity
              : distribution.minimum.value;
        }

        if (minimumMacroGrams > macroGrams) {
          result[key] = minimumMacroGrams;
          leftoverCaloriesAfterMinimums -= minimumMacroGrams * Nutrient.macroKcalPerGram[key];
          leftoverPercentageAfterMinimums -= distribution.leftoverPercentage;
        } else {
          nonMinimumLeftoverKeys.push(key);
        }
      }
    }

    if (leftoverCaloriesAfterMinimums < 0) {
      return new Error("gPerKg distributions and minimums exceed total calories");
    }

    // Update the non-minimum leftovers with what is remaining after minimums
    for (const key of nonMinimumLeftoverKeys) {
      const distribution = macroDistribution[key];
      if (distribution.type === "leftoverPercentage") {
        const calories =
          (leftoverCaloriesAfterMinimums * distribution.leftoverPercentage) /
          leftoverPercentageAfterMinimums;
        const macroGrams = calories / Nutrient.macroKcalPerGram[key];
        result[key] = macroGrams;
      }
    }

    return {
      energy: {
        type: "exact",
        value: Math.round(result.energy),
      },
      protein: {
        type: "exact",
        value: Math.round(result.protein),
      },
      carbohydrate: {
        type: "exact",
        value: Math.round(result.carbohydrate),
      },
      fat: {
        type: "exact",
        value: Math.round(result.fat),
      },
    };
  };
}
